import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatRut, validateRut, apiError } from "../lib/format";
import { toast } from "sonner";
import { LockKey, IdentificationCard, ArrowRight } from "@phosphor-icons/react";
import CMFFooter from "../components/CMFFooter";

const LOGIN_BG = "https://images.unsplash.com/photo-1561518658-db43bb106aeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMGZpbmFuY2lhbCUyMGJ1aWxkaW5nfGVufDB8fHx8MTc3Njc5MjU1OHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [rut, setRut] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/app";

  const onRutChange = (e) => setRut(formatRut(e.target.value));
  const onPinChange = (e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateRut(rut)) return toast.error("Ingresa un RUT chileno válido");
    if (pin.length !== 4) return toast.error("El PIN debe tener 4 dígitos");
    setBusy(true);
    try {
      await login(rut, pin);
      toast.success("Bienvenido de vuelta");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(apiError(err, "Error al iniciar sesión"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-bg">
      {/* Left: form */}
      <div className="flex flex-col px-6 sm:px-12 lg:px-20 py-10">
        <Link to="/" className="flex items-center gap-2 mb-16">
          <img src="/logo-activa.png" alt="Activa Capital Privado" className="w-10 h-10 object-contain shrink-0" />
          <div className="leading-tight">
            <div className="font-display font-black text-lg tracking-tight">Activa</div>
            <div className="label-mono">Capital Privado</div>
          </div>
        </Link>

        <div className="flex-1 flex flex-col justify-center max-w-md">          <div className="label-mono mb-3">01 / Acceso seguro</div>
          <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter mb-4 leading-[0.95]">
            Ingresa a tu<br /> banca digital.
          </h1>
          <p className="text-inkMuted mb-10">Usa tu RUT chileno y PIN de 4 dígitos para acceder a tu cuenta Activa.</p>

          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <label className="label-mono block mb-2">RUT</label>
              <div className="relative">
                <IdentificationCard size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-inkMuted" weight="duotone" />
                <input
                  data-testid="login-rut-input"
                  type="text"
                  autoComplete="username"
                  value={rut}
                  onChange={onRutChange}
                  placeholder="12.345.678-9"
                  className="w-full h-14 pl-12 pr-4 border border-hairline bg-surface font-mono text-lg focus:outline-none focus:border-forest focus:ring-1 focus:ring-forest"
                />
              </div>
            </div>

            <div>
              <label className="label-mono block mb-2">PIN (4 dígitos)</label>
              <div className="relative">
                <LockKey size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-inkMuted" weight="duotone" />
                <input
                  data-testid="login-pin-input"
                  type="password"
                  inputMode="numeric"
                  autoComplete="current-password"
                  value={pin}
                  onChange={onPinChange}
                  placeholder="••••"
                  className="w-full h-14 pl-12 pr-4 border border-hairline bg-surface font-mono text-2xl tracking-[0.5em] focus:outline-none focus:border-forest focus:ring-1 focus:ring-forest"
                />
              </div>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={busy}
              className="group w-full h-14 bg-forest text-bg font-display font-semibold tracking-wide flex items-center justify-center gap-2 hover:bg-forest-hover transition-colors disabled:opacity-60"
            >
              {busy ? "Ingresando…" : "Ingresar"}
              <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>

          <div className="mt-10 pt-8 border-t border-hairline text-sm text-inkMuted">
            ¿No tienes cuenta?{" "}
            <Link to="/register" className="text-forest font-semibold underline underline-offset-4 hover:text-burnt" data-testid="register-link">
              Abre la tuya
            </Link>
          </div>
        </div>

        <div className="label-mono mt-12">ACTIVA S.A. · RUT 76.724.250-6 · Santiago · Chile</div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative">
        <img src={LOGIN_BG} alt="Arquitectura financiera" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest/50 mix-blend-multiply" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-bg">
          <div className="label-mono text-bg/70 mb-3">// manifiesto</div>
          <div className="font-display text-3xl font-bold leading-tight max-w-md">
            "Inversiones que trabajan por ti: sólidas, transparentes y siempre a tu favor."
          </div>
          <div className="mt-6 flex items-center gap-3 text-sm">
            <div className="w-10 h-[1px] bg-bg/60" />
            <span className="font-mono">Santiago · Chile</span>
          </div>
        </div>
      </div>

      {/* CMF footer spans full width below both columns */}
      <div className="lg:col-span-2">
        <CMFFooter variant="compact" />
      </div>
    </div>
  );
}
